/**
 * Camera, screen, and voice recorder.
 * The preview is a canvas, and that same canvas is what gets saved,
 * so the picture-in-picture and effects match the file on the timeline.
 */

import { useEffect, useRef, useState } from 'react';
import { useVideoStore } from '@/stores/videoStore';
import {
  RECORD_BACKDROPS,
  RECORD_EFFECTS,
  RECORD_LIMIT_MS,
  coverRect,
  fitSize,
  insetBox,
  patchWebmDuration,
  pickRecorderMime,
  pipBox,
  recordClocks,
  type RecordBackdropId,
  type RecordEffectId,
} from '@/lib/video/recordSession';

export type RecordMode = 'camera' | 'screen' | 'voice';
type Phase = 'setup' | 'recording' | 'review';
type ToolMenu = 'camera' | 'mic' | 'backdrop' | 'effects' | null;

interface RecordStudioProps {
  mode: RecordMode;
  onClose: () => void;
  onRecorded: (file: File, kind: 'video' | 'audio', atMs: number, durationMs?: number) => void | Promise<void>;
}

export default function RecordStudio({ mode, onClose, onRecorded }: RecordStudioProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>('setup');
  const [menu, setMenu] = useState<ToolMenu>(null);
  const [cameraOn, setCameraOn] = useState(mode === 'camera');
  const [micOn, setMicOn] = useState(true);
  const [backdropId, setBackdropId] = useState<RecordBackdropId>('none');
  const [effectId, setEffectId] = useState<RecordEffectId>('none');
  const [scriptOpen, setScriptOpen] = useState(false);
  const [script, setScript] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const [takeUrl, setTakeUrl] = useState<string | null>(null);
  const [cams, setCams] = useState<MediaDeviceInfo[]>([]);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [level, setLevel] = useState(0);
  const [added, setAdded] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const captureTrack = useRef<MediaStreamTrack & { requestFrame?: () => void } | null>(null);
  const cameraVideo = useRef<HTMLVideoElement>(null);
  const screenVideo = useRef<HTMLVideoElement>(null);
  const cameraStream = useRef<MediaStream | null>(null);
  const micStream = useRef<MediaStream | null>(null);
  const screenStream = useRef<MediaStream | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const discardRef = useRef(false);
  const takeRef = useRef<Blob | null>(null);
  const placeAtMs = useRef(0);
  const durationRef = useRef(0);
  const placing = useRef(false);
  const startedPerf = useRef(0);
  const alive = useRef(true);
  const mixRef = useRef<AudioContext | null>(null);
  const levelCtx = useRef<AudioContext | null>(null);
  const levelFrame = useRef(0);
  const paintFrame = useRef(0);
  const phaseRef = useRef(phase);
  const look = useRef({ backdropId, effectId, cameraOn });
  phaseRef.current = phase;
  look.current = { backdropId, effectId, cameraOn };

  const release = (): void => {
    window.cancelAnimationFrame(paintFrame.current);
    window.cancelAnimationFrame(levelFrame.current);
    for (const stream of [cameraStream.current, micStream.current, screenStream.current]) {
      stream?.getTracks().forEach((track) => track.stop());
    }
    cameraStream.current = null;
    micStream.current = null;
    screenStream.current = null;
    void levelCtx.current?.close().catch(() => undefined);
    void mixRef.current?.close().catch(() => undefined);
    levelCtx.current = null;
    mixRef.current = null;
  };

  const refreshDevices = async (): Promise<void> => {
    const all = await navigator.mediaDevices.enumerateDevices();
    setCams(all.filter((device) => device.kind === 'videoinput'));
    setMics(all.filter((device) => device.kind === 'audioinput'));
  };

  const watchLevel = (stream: MediaStream): void => {
    window.cancelAnimationFrame(levelFrame.current);
    void levelCtx.current?.close();
    const context = new AudioContext();
    levelCtx.current = context;
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    context.createMediaStreamSource(stream).connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    const tick = (): void => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (const value of data) {
        const sample = (value - 128) / 128;
        sum += sample * sample;
      }
      setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
      levelFrame.current = window.requestAnimationFrame(tick);
    };
    tick();
  };

  const openCamera = async (deviceId?: string): Promise<void> => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'user' },
    });
    if (!alive.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    cameraStream.current?.getTracks().forEach((track) => track.stop());
    cameraStream.current = stream;
    const video = cameraVideo.current;
    if (video) {
      video.srcObject = stream;
      await video.play().catch(() => undefined);
    }
    setCameraOn(true);
    await refreshDevices();
  };

  const openMic = async (deviceId?: string): Promise<void> => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: deviceId ? { deviceId: { exact: deviceId } } : true,
    });
    if (!alive.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    micStream.current?.getTracks().forEach((track) => track.stop());
    micStream.current = stream;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = true;
    });
    setMicOn(true);
    watchLevel(stream);
    await refreshDevices();
  };

  const openScreen = async (): Promise<void> => {
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
    if (!alive.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    screenStream.current?.getTracks().forEach((track) => track.stop());
    screenStream.current = stream;
    const video = screenVideo.current;
    if (video) {
      video.srcObject = stream;
      await video.play().catch(() => undefined);
    }
    const [track] = stream.getVideoTracks();
    if (track) {
      track.onended = () => {
        screenStream.current = null;
        if (recRef.current?.state === 'recording') stopRecording(false);
      };
    }
  };

  const paint = (): void => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    if (phaseRef.current !== 'recording') {
      const screen = screenVideo.current;
      const size = screen && screen.videoWidth ? fitSize(screen.videoWidth, screen.videoHeight) : { w: 1280, h: 720 };
      if (canvas.width !== size.w || canvas.height !== size.h) {
        canvas.width = size.w;
        canvas.height = size.h;
      }
    }
    const backdrop = RECORD_BACKDROPS.find((item) => item.id === look.current.backdropId) ?? RECORD_BACKDROPS[0];
    const effect = RECORD_EFFECTS.find((item) => item.id === look.current.effectId) ?? RECORD_EFFECTS[0];
    const width = canvas.width;
    const height = canvas.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.filter = 'none';
    ctx.fillStyle = backdrop.color;
    ctx.fillRect(0, 0, width, height);
    ctx.filter = effect.filter;
    const screen = screenVideo.current;
    const camera = cameraVideo.current;
    const screenLive = Boolean(screen && screen.readyState >= 2 && screen.videoWidth);
    const cameraLive = look.current.cameraOn && Boolean(camera && camera.readyState >= 2 && camera.videoWidth);
    if (screenLive && screen) {
      const crop = coverRect(screen.videoWidth, screen.videoHeight, width, height);
      ctx.drawImage(screen, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, width, height);
    }
    if (cameraLive && camera) {
      const dest = screenLive ? pipBox(width, height) : backdrop.id === 'none' ? { x: 0, y: 0, w: width, h: height } : insetBox(width, height);
      const crop = coverRect(camera.videoWidth, camera.videoHeight, dest.w, dest.h);
      ctx.save();
      if (dest.w < width && typeof ctx.roundRect === 'function') {
        ctx.beginPath();
        ctx.roundRect(dest.x, dest.y, dest.w, dest.h, 12);
        ctx.clip();
      }
      ctx.drawImage(camera, crop.sx, crop.sy, crop.sw, crop.sh, dest.x, dest.y, dest.w, dest.h);
      ctx.restore();
    }
    ctx.filter = 'none';
    if (phaseRef.current === 'recording') captureTrack.current?.requestFrame?.();
  };

  const stopRecording = (discard: boolean): void => {
    discardRef.current = discard;
    const recorder = recRef.current;
    if (recorder && recorder.state === 'recording') {
      if (!discard) {
        try {
          recorder.requestData();
        } catch {
          /* The final chunk still arrives from stop(). */
        }
      }
      recorder.stop();
      return;
    }
    setPhase('setup');
    setElapsed(0);
  };

  const placeTake = async (blob: Blob, durationMs: number): Promise<void> => {
    if (placing.current || added) return;
    if (blob.size < 64) {
      setError('The recording was empty. Record a little longer and try again.');
      return;
    }
    placing.current = true;
    const type = (blob.type || (mode === 'voice' ? 'audio/webm' : 'video/webm')).split(';')[0].trim();
    const ext = type.includes('mp4') ? 'mp4' : 'webm';
    const name = mode === 'voice' ? `voiceover.${ext}` : mode === 'screen' ? `screen.${ext}` : `camera.${ext}`;
    const stamped = type.includes('webm') ? await patchWebmDuration(blob, durationMs) : blob;
    try {
      await onRecorded(new File([stamped], name, { type }), mode === 'voice' ? 'audio' : 'video', placeAtMs.current, durationMs);
      setAdded(true);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The recording could not be added to the timeline.');
    } finally {
      placing.current = false;
    }
  };

  const startRecording = async (): Promise<void> => {
    setError(null);
    setPending(true);
    setMenu(null);
    try {
      if (mode === 'screen' && !screenStream.current) await openScreen();
      if ((mode === 'camera' || cameraOn) && !cameraStream.current && mode !== 'voice') await openCamera();
      if (micOn && !micStream.current) await openMic();
      if (mode === 'voice' && !micStream.current) throw new Error('Turn on the microphone to record a voiceover.');
      const kind = mode === 'voice' ? 'audio' : 'video';
      const owned: MediaStreamTrack[] = [];
      let recorded: MediaStream;
      const cameraSource = cameraStream.current?.getVideoTracks()[0];
      if (mode === 'voice') {
        recorded = new MediaStream(micStream.current?.getAudioTracks() ?? []);
      } else if (mode === 'camera') {
        if (!cameraSource) throw new Error('Turn the camera on to record.');
        let video = cameraSource;
        try {
          video = cameraSource.clone();
          owned.push(video);
        } catch {
          video = cameraSource;
        }
        recorded = new MediaStream([video]);
        const mic = micOn ? micStream.current?.getAudioTracks()[0] : undefined;
        if (mic) {
          const audio = mic.clone();
          audio.enabled = true;
          recorded.addTrack(audio);
          owned.push(audio);
        }
        phaseRef.current = 'recording';
      } else {
        paint();
        phaseRef.current = 'recording';
        const canvas = canvasRef.current;
        const capture = canvas && 'captureStream' in canvas ? canvas.captureStream.bind(canvas) : null;
        if (!capture) throw new Error('This browser cannot record the preview.');
        const timed = capture(30);
        const [frameTrack] = timed.getVideoTracks();
        recorded = timed;
        captureTrack.current = frameTrack as MediaStreamTrack & { requestFrame?: () => void };
        owned.push(...recorded.getVideoTracks());
        const mixed = mixInto(recorded, micStream.current, screenStream.current, micOn);
        mixRef.current = mixed.context;
        owned.push(...mixed.owned);
      }
      const mime = pickRecorderMime(kind);
      const recorder =
        typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(mime)
          ? new MediaRecorder(recorded, { mimeType: mime })
          : new MediaRecorder(recorded);
      chunks.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) chunks.current.push(event.data);
      };
      recorder.onstop = () => {
        captureTrack.current = null;
        owned.forEach((track) => track.stop());
        void mixRef.current?.close().catch(() => undefined);
        mixRef.current = null;
        if (!alive.current) return;
        if (mode === 'voice') useVideoStore.getState().pause();
        if (discardRef.current) {
          discardRef.current = false;
          setPhase('setup');
          setElapsed(0);
          return;
        }
        const type = (recorder.mimeType || mime).split(';')[0] || mime;
        const blob = new Blob(chunks.current, { type });
        const durationMs = Math.max(100, Math.round(performance.now() - startedPerf.current));
        durationRef.current = durationMs;
        takeRef.current = blob;
        setTakeUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return URL.createObjectURL(blob);
        });
        setPhase('review');
        void placeTake(blob, durationMs);
      };
      placeAtMs.current = useVideoStore.getState().currentTimeMs;
      if (mode === 'voice') useVideoStore.getState().play();
      recorder.start(250);
      recRef.current = recorder;
      captureTrack.current?.requestFrame?.();
      startedPerf.current = performance.now();
      setElapsed(0);
      setPhase('recording');
    } catch (cause) {
      if (phase !== 'recording') phaseRef.current = phase;
      setError(deviceError(cause));
    } finally {
      setPending(false);
    }
  };

  const close = (): void => {
    alive.current = false;
    discardRef.current = true;
    if (mode === 'voice' && phaseRef.current === 'recording') useVideoStore.getState().pause();
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    release();
    setTakeUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    onClose();
  };

  const requestClose = (): void => {
    if (added) {
      close();
      return;
    }
    if (phase === 'recording' || takeRef.current) {
      setAskDiscard(true);
      return;
    }
    close();
  };

  const recordAgain = (): void => {
    setAskDiscard(false);
    takeRef.current = null;
    setTakeUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    if (phase === 'recording') stopRecording(true);
    else {
      setPhase('setup');
      setElapsed(0);
    }
  };

  const useTake = (): void => {
    if (added) {
      close();
      return;
    }
    const blob = takeRef.current;
    if (!blob) return;
    void placeTake(blob, durationRef.current || Math.max(100, elapsed));
  };

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    const boot = async (): Promise<void> => {
      try {
        if (mode === 'camera') await openCamera();
        if (!cancelled) await openMic();
      } catch (cause) {
        if (!cancelled) setError(deviceError(cause));
      }
    };
    void boot();
    return () => {
      cancelled = true;
      alive.current = false;
      discardRef.current = true;
      if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
      release();
    };
    // Device setup runs once per opening of the studio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (mode === 'voice' || phase === 'review') return;
    const tick = (): void => {
      paint();
      paintFrame.current = window.requestAnimationFrame(tick);
    };
    paintFrame.current = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(paintFrame.current);
    // paint reads the latest look and streams through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, phase]);

  useEffect(() => {
    if (phase !== 'recording') return;
    const timer = window.setInterval(() => {
      const next = performance.now() - startedPerf.current;
      setElapsed(next);
      if (next >= RECORD_LIMIT_MS) stopRecording(false);
    }, 200);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const clocks = recordClocks(elapsed);
  const title = phase === 'recording' ? 'Recording in progress' : phase === 'review' ? 'Review' : '';
  const showCamera = mode !== 'voice';

  return (
    <div className="vt-rec-backdrop" role="presentation" onMouseDown={requestClose}>
      <div
        className="vt-rec"
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Record'}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="vt-rec-head">
          <h2>{title}</h2>
          <button type="button" className="vt-rec-x" aria-label="Close" onClick={requestClose}>
            ×
          </button>
        </header>
        <div className="vt-rec-stage">
          <video ref={cameraVideo} className="vt-rec-source" muted playsInline />
          <video ref={screenVideo} className="vt-rec-source" muted playsInline />
          {phase === 'review' && takeUrl ? (
            mode === 'voice' ? (
              <audio className="vt-rec-playback" src={takeUrl} controls autoPlay />
            ) : (
              <video className="vt-rec-playback" src={takeUrl} controls autoPlay playsInline />
            )
          ) : (
            <canvas ref={canvasRef} width={1280} height={720} />
          )}
          {mode === 'voice' && phase !== 'review' ? (
            <div className="vt-rec-meter" aria-hidden="true">
              <span style={{ height: `${Math.round(12 + level * 88)}%` }} />
              <span style={{ height: `${Math.round(20 + level * 70)}%` }} />
              <span style={{ height: `${Math.round(8 + level * 90)}%` }} />
            </div>
          ) : null}
          {phase === 'setup' && mode === 'screen' ? (
            <p className="vt-rec-hint">Press Record, then choose the screen or window to share.</p>
          ) : null}
          {error ? <p className="vt-rec-error">{error}</p> : null}
          {phase === 'recording' && script.trim() ? <p className="vt-rec-prompter">{script}</p> : null}
          {phase === 'setup' ? (
            <button type="button" className="vt-rec-go" disabled={pending} onClick={() => void startRecording()}>
              <span />
              Record
            </button>
          ) : null}
          {phase === 'recording' ? (
            <div className="vt-rec-pill">
              <button type="button" className="vt-rec-stop" aria-label="Stop and review" onClick={() => stopRecording(false)}>
                <i />
              </button>
              <span className="vt-rec-elapsed">{clocks.elapsed}</span>
              <span className="vt-rec-remaining">{clocks.remaining}</span>
              <span className="vt-rec-rule" />
              <button type="button" className="vt-rec-restart" aria-label="Record again" onClick={recordAgain}>
                <Glyph name="restart" />
              </button>
            </div>
          ) : null}
        </div>
        {scriptOpen ? (
          <label className="vt-rec-script">
            Script
            <textarea
              value={script}
              maxLength={2000}
              placeholder="Write what you want to say. It stays on screen while you record and is not saved into the video."
              onChange={(event) => setScript(event.target.value)}
            />
          </label>
        ) : null}
        {phase === 'review' && added ? <p className="vt-rec-added">Added to the timeline.</p> : null}
        <div className="vt-rec-tools">
          {phase === 'review' || phase === 'recording' ? (
            <button type="button" className="vt-rec-again" onClick={recordAgain}>
              <Glyph name="restart" />
              Record again
            </button>
          ) : null}
          {showCamera ? (
            <ToolButton
              label="Camera"
              icon="camera"
              pressed={cameraOn}
              open={menu === 'camera'}
              onClick={() => setMenu((current) => (current === 'camera' ? null : 'camera'))}
            />
          ) : null}
          <ToolButton
            label="Mic"
            icon="mic"
            pressed={micOn}
            open={menu === 'mic'}
            onClick={() => setMenu((current) => (current === 'mic' ? null : 'mic'))}
          />
          {phase === 'setup' && showCamera ? (
            <ToolButton
              label="Backdrop"
              icon="backdrop"
              pressed={backdropId !== 'none'}
              open={menu === 'backdrop'}
              onClick={() => setMenu((current) => (current === 'backdrop' ? null : 'backdrop'))}
            />
          ) : null}
          <ToolButton label="Script" icon="script" pressed={scriptOpen} open={scriptOpen} onClick={() => setScriptOpen((open) => !open)} />
          {phase === 'setup' && showCamera ? (
            <ToolButton
              label="Effects"
              icon="effects"
              pressed={effectId !== 'none'}
              open={menu === 'effects'}
              onClick={() => setMenu((current) => (current === 'effects' ? null : 'effects'))}
            />
          ) : null}
          {phase === 'recording' ? (
            <button type="button" className="vt-rec-review" onClick={() => stopRecording(false)}>
              Review
              <span aria-hidden="true">›</span>
            </button>
          ) : null}
          {phase === 'review' ? (
            <button type="button" className="vt-rec-review" onClick={useTake}>
              {added ? 'Done' : 'Add to timeline'}
              <span aria-hidden="true">›</span>
            </button>
          ) : null}
        </div>
        {menu === 'camera' ? (
          <ChoiceList
            onPick={(id) => {
              setMenu(null);
              if (id === 'off') {
                setCameraOn(false);
                cameraStream.current?.getTracks().forEach((track) => track.stop());
                cameraStream.current = null;
                return;
              }
              if (id === 'on') {
                void openCamera().catch((cause) => setError(deviceError(cause)));
                return;
              }
              void openCamera(id).catch((cause) => setError(deviceError(cause)));
            }}
            options={[
              { id: cameraOn ? 'off' : 'on', label: cameraOn ? 'Turn camera off' : 'Turn camera on' },
              ...cams.map((device, index) => ({ id: device.deviceId || `cam-${index}`, label: device.label || `Camera ${index + 1}` })),
            ]}
          />
        ) : null}
        {menu === 'mic' ? (
          <ChoiceList
            onPick={(id) => {
              setMenu(null);
              if (id === 'off') {
                setMicOn(false);
                if (phase === 'recording') {
                  micStream.current?.getAudioTracks().forEach((track) => {
                    track.enabled = false;
                  });
                } else {
                  micStream.current?.getTracks().forEach((track) => track.stop());
                  micStream.current = null;
                  window.cancelAnimationFrame(levelFrame.current);
                  setLevel(0);
                }
                return;
              }
              if (phase === 'recording' && id !== 'on') {
                setError('The new microphone is used the next time you record.');
                return;
              }
              void openMic(id === 'on' ? undefined : id).catch((cause) => setError(deviceError(cause)));
            }}
            options={[
              { id: micOn ? 'off' : 'on', label: micOn ? 'Turn microphone off' : 'Turn microphone on' },
              ...mics.map((device, index) => ({ id: device.deviceId || `mic-${index}`, label: device.label || `Microphone ${index + 1}` })),
            ]}
          />
        ) : null}
        {menu === 'backdrop' ? (
          <ChoiceList
            onPick={(id) => {
              setBackdropId(id as RecordBackdropId);
              setMenu(null);
            }}
            options={RECORD_BACKDROPS.map((item) => ({ id: item.id, label: item.label }))}
          />
        ) : null}
        {menu === 'effects' ? (
          <ChoiceList
            onPick={(id) => {
              setEffectId(id as RecordEffectId);
              setMenu(null);
            }}
            options={RECORD_EFFECTS.map((item) => ({ id: item.id, label: item.label }))}
          />
        ) : null}
        {askDiscard ? (
          <div className="vt-rec-confirm">
            <p>Discard this take?</p>
            <button type="button" onClick={() => setAskDiscard(false)}>
              Keep it
            </button>
            <button type="button" onClick={close}>
              Discard
            </button>
          </div>
        ) : null}
        <p className="vt-rec-legal">
          By recording, you confirm you have permission to capture the people, screen, and audio in this take, and that you will respect the copyright in what you record.
        </p>
      </div>
    </div>
  );
}

function mixInto(
  target: MediaStream,
  mic: MediaStream | null,
  screen: MediaStream | null,
  micEnabled: boolean
): { context: AudioContext | null; owned: MediaStreamTrack[] } {
  const tracks: MediaStreamTrack[] = [];
  mic?.getAudioTracks().forEach((track) => {
    track.enabled = micEnabled;
    tracks.push(track);
  });
  if (screen) tracks.push(...screen.getAudioTracks());
  if (!tracks.length) return { context: null, owned: [] };
  if (tracks.length === 1) {
    target.addTrack(tracks[0]);
    return { context: null, owned: [] };
  }
  const context = new AudioContext();
  const dest = context.createMediaStreamDestination();
  for (const track of tracks) context.createMediaStreamSource(new MediaStream([track])).connect(dest);
  const owned = dest.stream.getAudioTracks();
  owned.forEach((track) => target.addTrack(track));
  return { context, owned };
}

function deviceError(cause: unknown): string {
  if (cause instanceof DOMException && cause.name === 'AbortError') {
    return 'The picker was closed before a camera, microphone, or screen was chosen.';
  }
  if (cause instanceof DOMException && cause.name === 'NotAllowedError') {
    return 'Permission was blocked. Allow the camera, microphone, or screen in the browser and try again.';
  }
  if (cause instanceof DOMException && cause.name === 'NotFoundError') {
    return 'No matching camera or microphone was found.';
  }
  if (cause instanceof Error && cause.message) return cause.message;
  return 'Recording could not start.';
}

function ToolButton({
  label,
  icon,
  pressed,
  open,
  onClick,
}: {
  label: string;
  icon: 'camera' | 'mic' | 'backdrop' | 'script' | 'effects';
  pressed: boolean;
  open: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" className={`vt-rec-tool ${pressed ? 'on' : ''}`} aria-pressed={pressed} aria-expanded={open} onClick={onClick}>
      <Glyph name={icon} />
      <span>
        {label}
        {icon === 'camera' || icon === 'mic' ? <i className="vt-rec-chevron" /> : null}
      </span>
    </button>
  );
}

function ChoiceList({
  options,
  onPick,
}: {
  options: { id: string; label: string }[];
  onPick: (id: string) => void;
}): JSX.Element {
  return (
    <div className="vt-rec-menu" role="menu">
      {options.map((option) => (
        <button key={option.id} type="button" role="menuitem" onClick={() => onPick(option.id)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Glyph({ name }: { name: string }): JSX.Element {
  const common = { viewBox: '0 0 24 24', width: 22, height: 22, fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (name === 'camera') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M4 8h4l2-2h4l2 2h4v10H4z" />
        <circle cx="12" cy="13" r="3" />
      </svg>
    );
  }
  if (name === 'mic') {
    return (
      <svg {...common} aria-hidden="true">
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M6 11a6 6 0 0 0 12 0M12 17v3" />
      </svg>
    );
  }
  if (name === 'backdrop') {
    return (
      <svg {...common} aria-hidden="true">
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M4 15l4-3 3 2 3-4 6 5" />
      </svg>
    );
  }
  if (name === 'script') {
    return (
      <svg {...common} aria-hidden="true">
        <rect x="6" y="3" width="12" height="18" rx="2" />
        <path d="M9 8h6M9 12h6M9 16h4" />
      </svg>
    );
  }
  if (name === 'effects') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6zM18 14l.7 1.8L20.5 16.5 18.7 17.2 18 19l-.7-1.8-1.8-.7 1.8-.7z" />
      </svg>
    );
  }
  return (
    <svg {...common} aria-hidden="true">
      <path d="M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4" />
    </svg>
  );
}
